import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Icon, ModalFooter, ModalHeader } from '../common';
import { useAuth } from '../../contexts/AuthContext';
import { useAppNavigation } from '../../contexts/AppNavigationContext';
import { getVisibleNavigationItems, NavigationContext, NavigationItemDefinition } from '../../config/navigation';
import familyTreeLogo from '../../assets/family-tree-logo.png';
import { brandClasses } from '../../theme';
import { LanguageSelector } from '../LanguageSelector';

interface MainLayoutProps {
  children: React.ReactNode;
  fullBleed?: boolean;
  showFooter?: boolean;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children, fullBleed = false, showFooter = true }) => {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { activeTreeId, canManageMembers, canManagePublicTree } = useAppNavigation();

  const navigationContext = useMemo<NavigationContext>(() => ({
    systemRole: user?.systemRole,
    lastOpenedTreeId: user?.lastOpenedTreeId,
    activeTreeId,
    canManageMembers,
    canManagePublicTree,
  }), [activeTreeId, canManageMembers, canManagePublicTree, user?.lastOpenedTreeId, user?.systemRole]);

  const visibleNavItems = useMemo(() => {
    return getVisibleNavigationItems(navigationContext);
  }, [navigationContext]);

  const isActive = (item: NavigationItemDefinition) => {
    return item.isActive?.(location.pathname, navigationContext) ?? false;
  };

  const handleLogoutRequest = () => {
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
    setIsMobileMenuOpen(false);
    navigate('/login', { replace: true });
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const renderNavItem = (item: NavigationItemDefinition, mobile = false) => {
    const active = isActive(item);
    const itemLabel = t(`items.${item.id}`, { ns: 'navigation', defaultValue: item.label });
    const classes = `
      flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
      ${
        active
          ? brandClasses.navActive
          : brandClasses.navIdle
      }
      ${mobile ? 'w-full' : ''}
    `;

    if (item.isAction) {
      return (
        <button
          key={item.id}
          type="button"
          onClick={handleLogoutRequest}
          className={classes}
          aria-label={itemLabel}
        >
          <Icon name={item.icon} size="md" />
          <span>{itemLabel}</span>
        </button>
      );
    }

    const path = item.getPath?.(navigationContext) ?? '#';
    return (
      <Link
        key={item.id}
        to={path}
        onClick={() => setIsMobileMenuOpen(false)}
        className={classes}
        aria-current={active ? 'page' : undefined}
      >
        <Icon name={item.icon} size="md" />
        <span>{itemLabel}</span>
      </Link>
    );
  };

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
    || t('account.fullNameFallback', { ns: 'common' });

  return (
    <div className={`min-h-screen flex flex-col ${brandClasses.appShell}`}>
      <header className={`sticky top-0 z-40 backdrop-blur ${brandClasses.headerSurface} border-b`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/trees" className={`flex items-center rounded-xl px-1 py-1 ${brandClasses.appShell}`}>
              <img
                src={familyTreeLogo}
                alt="Family Tree"
                className="h-12 w-auto sm:h-14"
              />
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              {visibleNavItems.map((item) => renderNavItem(item))}
            </nav>

            <div className="flex items-center gap-4">
              <div className="hidden lg:block">
                <LanguageSelector compact />
              </div>

              <div className={`hidden sm:flex items-center gap-3 ps-4 border-s ${brandClasses.subtleBorder}`}>
                <div className="text-end">
                  <p className={`text-sm font-medium ${brandClasses.primaryText}`}>
                    {displayName}
                  </p>
                  <p className={`text-xs ${brandClasses.subtleText}`}>{user?.email}</p>
                </div>
              </div>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`md:hidden rounded-lg p-2 transition-colors ${brandClasses.hoverSurface}`}
                aria-label={t('actions.toggleMenu', { ns: 'navigation' })}
              >
                <Icon name={isMobileMenuOpen ? 'close' : 'menu'} size="md" className={brandClasses.textInteractive} />
              </button>
            </div>
          </div>

          {isMobileMenuOpen && (
            <nav className={`md:hidden space-y-2 border-t py-4 ${brandClasses.subtleBorder}`}>
              {visibleNavItems.map((item) => renderNavItem(item, true))}

              <div className={`mt-4 border-t pt-4 ${brandClasses.subtleBorder}`}>
                <div className="px-3 pb-4">
                  <LanguageSelector />
                </div>
                <div className="px-3 py-2">
                  <p className={`text-sm font-medium ${brandClasses.primaryText}`}>
                    {displayName}
                  </p>
                  <p className={`text-xs ${brandClasses.subtleText}`}>{user?.email}</p>
                </div>
              </div>
            </nav>
          )}
        </div>
      </header>

      <main className={`flex-1 ${fullBleed ? 'flex flex-col min-h-0' : ''}`}>
        {fullBleed ? (
          <div className="flex-1 flex flex-col min-h-0">{children}</div>
        ) : (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
        )}
      </main>

      {showFooter && (
        <footer className={`mt-auto border-t ${brandClasses.footerSurface}`}>
          <div className={`max-w-7xl mx-auto px-4 py-4 text-center text-sm sm:px-6 lg:px-8 ${brandClasses.subtleText}`}>
            <p>{t('app.copyright', { ns: 'common' })}</p>
          </div>
        </footer>
      )}

      {showLogoutConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('logoutDialog.ariaLabel', { ns: 'navigation' })}
          className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(26,23,20,0.45)] px-4 py-6"
          onClick={handleCancelLogout}
        >
          <div
            className="w-full max-w-sm overflow-hidden rounded-[24px] border border-[#E2DDD8] bg-[#FFFFFF] shadow-[0_24px_60px_rgba(26,23,20,0.24)]"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader
              eyebrow={t('logoutDialog.eyebrow', { ns: 'navigation' })}
              title={t('logoutDialog.title', { ns: 'navigation' })}
              description={t('logoutDialog.description', { ns: 'navigation' })}
              onClose={handleCancelLogout}
              closeAriaLabel={t('logoutDialog.closeAriaLabel', { ns: 'navigation' })}
            />
            <ModalFooter>
              <Button
                onClick={handleCancelLogout}
                variant="outline"
                className="sm:min-w-[120px]"
              >
                {t('buttons.cancel', { ns: 'common' })}
              </Button>
              <Button
                onClick={handleConfirmLogout}
                variant="danger"
                className="sm:min-w-[140px]"
              >
                {t('buttons.logOut', { ns: 'common' })}
              </Button>
            </ModalFooter>
          </div>
        </div>
      )}
    </div>
  );
};
